import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const GUIDE_URL = "https://heyzine.com/flip-book/332655054e.html";

export default function LeadMagnetModal({ open, onOpenChange }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setName(""); setEmail(""); setDone(false); setSubmitting(false);
  };

  const openGuide = () => {
    window.open(GUIDE_URL, "_blank", "noopener,noreferrer");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/newsletter/subscribe", {
        name, email, source: "new_agent_success_playbook",
      });
      setDone(true);
      toast.success("Opening your guide in a new tab...");
      // Auto-open the guide right after submission
      openGuide();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg rounded-2xl p-0 overflow-hidden border-0" data-testid="lead-magnet-modal" aria-describedby="lead-magnet-desc">
        <DialogTitle className="sr-only">Get the New Agent Success Playbook</DialogTitle>
        <DialogDescription id="lead-magnet-desc" className="sr-only">
          Subscribe with your name and email to access Tammy's free New Agent Success Playbook and join the weekly newsletter.
        </DialogDescription>
        <div className="grid sm:grid-cols-5">
          <div className="hidden sm:block sm:col-span-2 bg-gradient-to-br from-primary/20 to-secondary/30 relative">
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <img
                src="https://customer-assets.emergentagent.com/job_property-mentor-4/artifacts/lzc2u2uz_Screenshot%202026-06-03%20at%2011.33.21.png"
                alt="New Agent Success Playbook cover"
                className="rounded-lg shadow-lg w-full h-auto object-cover rotate-[-3deg]"
              />
            </div>
          </div>

          <div className="sm:col-span-3 p-8">
            {done ? (
              <div className="text-center py-6" data-testid="lead-magnet-success">
                <CheckCircle2 className="mx-auto text-primary" size={44} strokeWidth={1.5} />
                <h3 className="font-serif text-2xl mt-4">You're in!</h3>
                <p className="text-sm text-foreground/70 mt-3 leading-relaxed">
                  Your <strong>New Agent Success Playbook</strong> just opened in a
                  new tab. If your browser blocked the pop-up, click below.
                </p>
                <Button
                  onClick={openGuide}
                  className="mt-5 w-full rounded-full bg-primary hover:bg-primary/90 btn-lift h-11"
                  data-testid="lead-magnet-open-guide-btn"
                >
                  Open the guide <ExternalLink className="ml-2" size={14} />
                </Button>
                <Button
                  onClick={() => { reset(); onOpenChange(false); }}
                  variant="ghost"
                  className="mt-2 w-full rounded-full h-10 text-foreground/60 hover:text-foreground"
                  data-testid="lead-magnet-close-btn"
                >
                  Close
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="lead-magnet-form">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-primary">
                  <ArrowRight size={12} /> Free Resource
                </div>
                <h3 className="font-serif text-3xl mt-3 leading-tight">
                  Get the New Agent Success Playbook.
                </h3>
                <p className="text-sm text-foreground/70 mt-3 leading-relaxed">
                  Tammy's curated playbook for new agents — getting started,
                  buyer & seller systems, and referrals and resources.
                </p>

                <div className="space-y-3 mt-6">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70">First name</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="rounded-xl h-11 mt-2"
                      placeholder="Jane"
                      data-testid="lead-magnet-name"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70">Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-xl h-11 mt-2"
                      placeholder="jane@example.com"
                      data-testid="lead-magnet-email"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-magnet-submit"
                  className="w-full mt-5 rounded-full bg-primary hover:bg-primary/90 btn-lift h-12"
                >
                  {submitting ? (
                    <><Loader2 className="animate-spin mr-2" size={16} /> One moment...</>
                  ) : (
                    <>Take me there <ArrowRight className="ml-2" size={16} /></>
                  )}
                </Button>
                <p className="text-[10px] text-center text-foreground/50 mt-3 leading-relaxed">
                  No spam, ever. Unsubscribe with one click. Joining adds you to
                  Tammy's weekly note.
                </p>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
